import { ReloadOutlined } from '@ant-design/icons';
import type { OpsPerfMetric, OpsPerfPeriod, OpsPerfRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Segmented, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';
import { sumWidth } from '@hq/ui-kit';

const METRICS: { label: string; value: OpsPerfMetric }[] = [
  { label: '配送', value: 'DELIVER_COUNT' },
  { label: '拣货', value: 'PICK_COUNT' },
  { label: '复核', value: 'PACK_COUNT' },
  { label: '入库验收', value: 'INBOUND_LINES' }
];

const PERIODS: { label: string; value: OpsPerfPeriod }[] = [
  { label: '昨日', value: 'DAY' },
  { label: '本月', value: 'MONTH' }
];

/**
 * 运营人效排行（R14/DR-13.1）：读取 Worker 每小时聚合的 `ops_perf_stat`，
 * 按指标展示员工工作量、标准工时、相对人效与名次。
 */
export default function OpsPerfPage() {
  const [period, setPeriod] = useState<OpsPerfPeriod>('DAY');
  const [metric, setMetric] = useState<OpsPerfMetric>('DELIVER_COUNT');

  const query = useQuery({
    queryKey: ['ops', 'perf', period, metric],
    queryFn: () => hq.opsPerf.rank({ periodType: period, metricCode: metric, limit: 50 }),
    staleTime: 60 * 1000
  });

  const columns: ColumnsType<OpsPerfRow> = [
    {
      title: '名次',
      dataIndex: 'rankNo',
      width: 80,
      render: (value: number) =>
        value === 1 ? <Tag color="gold">第 1</Tag> : <Typography.Text>第 {value}</Typography.Text>
    },
    { title: '员工', dataIndex: 'userName', width: 140, render: (value: string | null, row) => value || `#${row.userId}` },
    { title: '机构', dataIndex: 'orgId', width: 120, render: (value: string) => `#${value}` },
    { title: '工作量（单）', dataIndex: 'workloadQty', width: 120, align: 'right', render: (value: number) => value.toFixed(0) },
    { title: '标准工时（分钟）', dataIndex: 'standardMinutes', width: 140, align: 'right' },
    {
      title: '相对人效',
      dataIndex: 'efficiencyRate',
      width: 120,
      align: 'right',
      render: (value: number) => {
        const percent = value * 100;
        return (
          <Typography.Text type={percent >= 100 ? 'success' : 'warning'}>
            {(value || 0).toFixed(2)}×
          </Typography.Text>
        );
      }
    },
    { title: '质量分', dataIndex: 'qualityScore', width: 100, align: 'right', render: (value: number) => value.toFixed(0) },
    { title: '统计日期', dataIndex: 'statDate', width: 130 }
  ];

  const rows = query.data ?? [];

  return (
    <Card
      className="page-card"
      title="运营人效排行"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented<OpsPerfPeriod> value={period} options={PERIODS} onChange={(value) => setPeriod(value)} />
        <Segmented<OpsPerfMetric> value={metric} options={METRICS} onChange={(value) => setMetric(value)} />
        <Typography.Text type="secondary">
          {period === 'DAY' ? '统计昨日完整一天' : '统计本月 1 日至今日'}
        </Typography.Text>
      </Space>

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="人效数据加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<OpsPerfRow>
        rowKey={(row) => `${row.orgId}-${row.userId}-${row.metricCode}`}
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{
          emptyText: (
            <Empty description="暂无聚合数据：Worker 将在下一轮（每小时）写入，或该周期内尚无已闭环作业" />
          )
        }}
      />
    </Card>
  );
}
